/**
 * Instance Admin → Marketplace → "Get it built — payment terms": the STANDARD
 * terms every custom-build order is placed under (plan D4–D8, D13). Each stage
 * is a share of the quoted price; the client pays Twasta a stage's share, and
 * Twasta pays the builder when the client approves it. Twasta's defaults live
 * in backend/app/data/marketplace/defaults.json (`build`); this screen stores
 * only what differs. Orders already placed keep the terms they were placed
 * under.
 *
 * Doc: docs/marketplace_get_it_built.md
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import {
  getAdminBuildTerms, money, previewAdminBuildTerms, saveAdminBuildTerms,
  type BuildStage, type BuildTerms, type BuildTermsView,
} from '@/api/marketplace'
import PaymentStages from '@/pages/marketplace/build/PaymentStages'
import { InfoBox, Section, Td, Th, chipStyle, errText, tableStyle } from './adminUi'

const EXAMPLE_DEFAULT_MAJOR = 240000   // the worked example only — not a rule

function clone<T>(v: T): T { return JSON.parse(JSON.stringify(v)) }

/** Keys whose value differs from Twasta's defaults (what gets stored). */
function diff(draft: BuildTerms, defaults: BuildTerms): Partial<BuildTerms> {
  const out: Record<string, unknown> = {}
  for (const k of Object.keys(draft) as (keyof BuildTerms)[]) {
    if (JSON.stringify(draft[k]) !== JSON.stringify(defaults[k])) out[k] = draft[k]
  }
  return out as Partial<BuildTerms>
}

function slug(name: string, taken: Set<string>): string {
  const base = name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 24) || 'stage'
  let key = base
  let n = 2
  while (taken.has(key)) { key = `${base}${n}`; n += 1 }
  return key
}

export default function BuildTermsPanel() {
  const [view, setView] = useState<BuildTermsView | null>(null)
  const [draft, setDraft] = useState<BuildTerms | null>(null)
  const [exampleMajor, setExampleMajor] = useState(EXAMPLE_DEFAULT_MAJOR)
  const [preview, setPreview] = useState<BuildTermsView | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const exampleMinor = Math.max(0, Math.round(exampleMajor * 100))

  const load = useCallback(() => {
    getAdminBuildTerms(exampleMinor)
      .then((v) => { setView(v); setDraft(clone(v.terms)); setPreview(v); setProblem(null) })
      .catch((e) => toast.error(errText(e, 'Could not load the payment terms')))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(load, [load])

  const override = useMemo(() => (draft && view ? diff(draft, view.defaults) : {}), [draft, view])
  const changed = Object.keys(override).length

  // Live check + worked example as the admin edits.
  useEffect(() => {
    if (!draft || !view) return
    const t = window.setTimeout(() => {
      previewAdminBuildTerms(override, exampleMinor)
        .then((v) => { setPreview(v); setProblem(null) })
        .catch((e) => setProblem(errText(e, 'These terms are not valid')))
    }, 300)
    return () => window.clearTimeout(t)
  }, [override, exampleMinor, draft, view])

  if (!view || !draft) return <div style={{ color: 'var(--color-text-secondary)' }}>Loading…</div>

  const set = <K extends keyof BuildTerms>(k: K, v: BuildTerms[K]) => setDraft({ ...draft, [k]: v })
  const setStage = (i: number, patch: Partial<BuildStage>) => {
    const stages = draft.stages.map((s, j) => (j === i ? { ...s, ...patch } : s))
    set('stages', stages)
  }
  const last = draft.stages.length - 1
  const total = draft.stages.reduce((a, s) => a + (Number(s.pct) || 0), 0)
  const currencies = Object.keys(view.defaults.tds_percent || {})
  const cur = preview?.currency || view.currency

  const save = async (reset = false) => {
    setSaving(true)
    try {
      const v = await saveAdminBuildTerms(reset ? {} : override, exampleMinor)
      setView(v); setDraft(clone(v.terms)); setPreview(v); setProblem(null)
      toast.success(reset ? 'Back to Twasta’s default payment terms' : 'Payment terms saved')
    } catch (e) {
      toast.error(errText(e, 'Could not save the payment terms'))
    } finally {
      setSaving(false)
    }
  }

  const choice = (on: boolean): React.CSSProperties => ({
    display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start', textAlign: 'left', padding: '12px 14px',
    borderRadius: 12, cursor: 'pointer', font: 'inherit', color: 'var(--color-text)', width: '100%',
    background: on ? 'var(--color-accent-weak)' : 'var(--color-bg-secondary)',
    border: `2px solid ${on ? 'var(--color-accent)' : 'var(--color-border)'}`,
  })
  const num = (value: number, onChange: (n: number) => void, label: string, width = 110) => (
    <input type="number" aria-label={label} value={Number.isFinite(value) ? value : ''} style={{ width }}
      onChange={(e) => onChange(e.target.value === '' ? NaN : Number(e.target.value))} />
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ maxWidth: 760, fontSize: 14, color: 'var(--color-text-secondary)' }}>
          The standard payment terms for every app built through <strong style={{ color: 'var(--color-text)' }}>Get it built</strong>.
          Builders see them before they quote one total price; clients agree to them when they order. Nobody can change them per order.
          Changes apply to orders placed after you save — orders already placed keep the terms they were placed under.
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={chipStyle}>{changed ? `${changed} changed from Twasta defaults` : 'Using Twasta defaults'}</span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => save(true)} disabled={saving || (!changed && !Object.keys(view.override || {}).length)}>Reset to defaults</button>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => save(false)} disabled={saving || !!problem}>{saving ? 'Saving…' : 'Save payment terms'}</button>
        </div>
      </div>
      {problem && <InfoBox><span style={{ color: 'var(--color-error)', fontWeight: 600 }}>{problem}</span></InfoBox>}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-start' }}>
        <div style={{ flex: '999 1 620px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <Section title="Payment stages">
            <div style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 12 }}>
              Each stage is a share of the estimate. The client pays Twasta a stage’s share {draft.collect_on === 'submit' ? 'when the builder submits it' : 'when they approve it'}; Twasta pays the builder when the client approves it. The advance is always first and sign-off is always last.
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ ...tableStyle, minWidth: 720 }}>
                <thead><tr><Th>#</Th><Th>Stage</Th><Th>The client approves</Th><Th right>Share %</Th><Th right>On {money(exampleMinor, cur)}</Th><Th /></tr></thead>
                <tbody>
                  {draft.stages.map((s, i) => (
                    <tr key={`${s.key}-${i}`}>
                      <Td style={{ color: 'var(--color-text-secondary)' }}>{i === 0 ? '·' : i}</Td>
                      <Td><input aria-label={`Stage ${i + 1} name`} value={s.name} maxLength={60} onChange={(e) => setStage(i, { name: e.target.value })} /></Td>
                      <Td>{i === 0
                        ? <span style={{ color: 'var(--color-text-secondary)', fontSize: 14 }}>Nothing — paid when the order is placed</span>
                        : <input aria-label={`What is approved at stage ${i}`} value={s.approves || ''} maxLength={160} onChange={(e) => setStage(i, { approves: e.target.value })} />}</Td>
                      <Td right>{num(s.pct, (n) => setStage(i, { pct: n }), `Share for ${s.name}`, 90)}</Td>
                      <Td right>{money(preview?.example[i]?.amount_minor ?? null, cur)}</Td>
                      <Td right>
                        <span style={{ display: 'inline-flex', gap: 6 }}>
                          <button type="button" className="btn btn-secondary btn-sm" aria-label={`Move ${s.name} up`} disabled={i <= 1 || i === last}
                            onClick={() => { const st = [...draft.stages]; [st[i - 1], st[i]] = [st[i], st[i - 1]]; set('stages', st) }}>↑</button>
                          <button type="button" className="btn btn-secondary btn-sm" aria-label={`Remove ${s.name}`} disabled={i === 0 || i === last}
                            onClick={() => set('stages', draft.stages.filter((_, j) => j !== i))}>✕</button>
                        </span>
                      </Td>
                    </tr>
                  ))}
                  <tr>
                    <Td>{null}</Td><Td><strong>Total</strong></Td><Td>{null}</Td>
                    <Td right><strong style={{ color: total === 100 ? 'var(--color-success)' : 'var(--color-error)' }}>{total}%</strong></Td>
                    <Td right><strong>{money(Math.round((exampleMinor * total) / 100), cur)}</strong></Td><Td>{null}</Td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
              <button type="button" className="btn btn-secondary btn-sm" disabled={draft.stages.length >= draft.max_stages}
                onClick={() => {
                  const st = [...draft.stages]
                  st.splice(last, 0, { key: slug('new stage', new Set(st.map((x) => x.key))), name: 'New stage', approves: '', pct: 0 })
                  set('stages', st)
                }}>+ Add a stage</button>
              <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Up to {draft.max_stages} stages · sign-off keeps at least {draft.signoff_min_pct}%</span>
            </div>
          </Section>

          <Section title="Collecting, approving and paying">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>When the advance is paid to the builder</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>
                  <button type="button" style={choice(draft.advance_release === 'first_approval')} aria-pressed={draft.advance_release === 'first_approval'} onClick={() => set('advance_release', 'first_approval')}>
                    <strong>With the first approval</strong><span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Twasta holds it until the client approves the first stage.</span>
                  </button>
                  <button type="button" style={choice(draft.advance_release === 'builder_accepts')} aria-pressed={draft.advance_release === 'builder_accepts'} onClick={() => set('advance_release', 'builder_accepts')}>
                    <strong>When the builder accepts the order</strong><span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>The builder gets it before starting work.</span>
                  </button>
                </div>
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>When a stage’s share is collected from the client</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>
                  <button type="button" style={choice(draft.collect_on === 'submit')} aria-pressed={draft.collect_on === 'submit'} onClick={() => set('collect_on', 'submit')}>
                    <strong>When the builder submits it (recommended)</strong><span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>The money is with Twasta before the client approves, so the builder is paid at once.</span>
                  </button>
                  <button type="button" style={choice(draft.collect_on === 'approve')} aria-pressed={draft.collect_on === 'approve'} onClick={() => set('collect_on', 'approve')}>
                    <strong>When the client approves it</strong><span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>The builder waits until the client’s payment clears.</span>
                  </button>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>Days the client has to approve a stage{num(draft.approve_days, (n) => set('approve_days', n), 'Days to approve')}</label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>If the client doesn’t respond
                  <select value={draft.on_no_response} onChange={(e) => set('on_no_response', e.target.value as BuildTerms['on_no_response'])}>
                    <option value="auto_approve">Remind, then approve automatically</option>
                    <option value="support">Remind, then pass it to Twasta support</option>
                  </select>
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>Pay the builder after approval (hours){num(draft.release_delay_hours, (n) => set('release_delay_hours', n), 'Release delay hours')}</label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>Builder has to accept an order within (hours){num(draft.accept_window_hours, (n) => set('accept_window_hours', n), 'Accept window hours')}</label>
              </div>
            </div>
          </Section>

          <Section title="Fees and tax">
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>Client service fee (%){num(draft.client_fee_percent, (n) => set('client_fee_percent', n), 'Client service fee')}<span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Tax was charged when the wallet was topped up.</span></label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>Builder fee (%){num(draft.builder_fee_percent, (n) => set('builder_fee_percent', n), 'Builder fee')}</label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>Tax on the builder fee
                <select value={draft.builder_fee_tax} onChange={(e) => set('builder_fee_tax', e.target.value as BuildTerms['builder_fee_tax'])}>
                  <option value="added">Added on top (from the platform tax rules)</option>
                  <option value="included">Included in the fee</option>
                </select>
              </label>
              {currencies.map((c) => (
                <label key={c} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>TDS on payouts — {c} (%)
                  {num(draft.tds_percent[c] ?? 0, (n) => set('tds_percent', { ...draft.tds_percent, [c]: n }), `TDS ${c}`)}</label>
              ))}
            </div>
          </Section>

          <Section title="Limits">
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>Sign-off keeps at least (%){num(draft.signoff_min_pct, (n) => set('signoff_min_pct', n), 'Sign-off minimum')}</label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>Most stages{num(draft.max_stages, (n) => set('max_stages', n), 'Most stages')}</label>
              {currencies.map((c) => (
                <label key={c} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>Smallest stage — {c}
                  {num((draft.min_stage_minor[c] ?? 0) / 100, (n) => set('min_stage_minor', { ...draft.min_stage_minor, [c]: Math.round(n * 100) }), `Smallest stage ${c}`, 130)}</label>
              ))}
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>Quotes valid for (days){num(draft.quote_valid_days, (n) => set('quote_valid_days', n), 'Quote validity')}</label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>Free fixes after sign-off (days){num(draft.fix_window_days, (n) => set('fix_window_days', n), 'Fix window')}</label>
            </div>
          </Section>
        </div>

        <aside style={{ flex: '1 1 360px', maxWidth: 460, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Section title="What the client and builder see">
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, marginBottom: 12 }}>
              Example estimate ({cur})
              <input type="number" aria-label="Example estimate" value={exampleMajor} min={0} style={{ width: 140 }} onChange={(e) => setExampleMajor(Number(e.target.value) || 0)} />
            </label>
            <PaymentStages stages={draft.stages} rows={preview?.example} amountMinor={exampleMinor} currency={cur} who="admin" advanceRelease={draft.advance_release} />
            {!!preview?.example.length && (
              <div style={{ overflowX: 'auto', marginTop: 14 }}>
                <table style={{ ...tableStyle, fontSize: 13 }}>
                  <thead><tr><Th>Stage</Th><Th right>Client pays</Th><Th right>Builder gets</Th></tr></thead>
                  <tbody>
                    {preview.example.map((r) => (
                      <tr key={r.index}>
                        <Td>{r.name}</Td>
                        <Td right>{money(r.client.total_minor, cur)}</Td>
                        <Td right>{money(r.builder.net_minor, cur)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 8 }}>
                  Client pays = share + {preview.terms.client_fee_percent}% service fee. Builder gets = share − {preview.terms.builder_fee_percent}% fee
                  {preview.terms.builder_fee_tax === 'added' ? ' − tax on the fee' : ''} − TDS.
                </div>
              </div>
            )}
          </Section>
        </aside>
      </div>
    </div>
  )
}
